import React, { useState, useEffect, useMemo, useCallback } from "react";
import { motion, AnimatePresence } from "motion/react";
import { DollarSign, Check, X, Mail, Download, Search } from "lucide-react";
import { supabase } from "../../supabaseClient";

const PaymentTracker = ({ team, players, season }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, paid, unpaid
  const [searchTerm, setSearchTerm] = useState("");

  // Load payments
  const loadPayments = useCallback(async () => {
    if (!season?.id || !players?.length) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("player_payments")
        .select("*")
        .eq("season_id", season.id)
        .in(
          "player_id",
          players.map((p) => p.id),
        );

      if (error) throw error;

      // Create payment records for players who don't have one
      const existingPlayerIds = new Set(data?.map((p) => p.player_id) || []);
      const missingPlayers = players.filter(
        (p) => !existingPlayerIds.has(p.id),
      );

      if (missingPlayers.length > 0) {
        const newPayments = missingPlayers.map((player) => ({
          player_id: player.id,
          season_id: season.id,
          amount: 0,
          paid: false,
        }));

        const { data: inserted } = await supabase
          .from("player_payments")
          .insert(newPayments)
          .select();

        setPayments([...(data || []), ...(inserted || [])]);
      } else {
        setPayments(data || []);
      }
    } catch (error) {
      console.error("Error loading payments:", error);
    } finally {
      setLoading(false);
    }
  }, [season.id, players]);

  useEffect(() => {
    if (!team?.id || !season?.id) return;
    loadPayments();
  }, [team?.id, season?.id, loadPayments]);

  const togglePaid = async (paymentId, currentStatus) => {
    const updates = {
      paid: !currentStatus,
      paid_date: !currentStatus ? new Date().toISOString().split("T")[0] : null,
    };

    const { error } = await supabase
      .from("player_payments")
      .update(updates)
      .eq("id", paymentId);

    if (!error) {
      setPayments((prev) =>
        prev.map((p) => (p.id === paymentId ? { ...p, ...updates } : p)),
      );
    }
  };

  const updatePayment = async (paymentId, field, value) => {
    const { error } = await supabase
      .from("player_payments")
      .update({ [field]: value })
      .eq("id", paymentId);

    if (!error) {
      setPayments((prev) =>
        prev.map((p) => (p.id === paymentId ? { ...p, [field]: value } : p)),
      );
    }
  };

  // Calculate stats
  const stats = useMemo(() => {
    const totalPlayers = players.length;
    const paidCount = payments.filter((p) => p.paid).length;
    const unpaidCount = totalPlayers - paidCount;
    const totalCollected = payments
      .filter((p) => p.paid)
      .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    const totalExpected = payments.reduce(
      (sum, p) => sum + (parseFloat(p.amount) || 0),
      0,
    );

    return {
      totalPlayers,
      paidCount,
      unpaidCount,
      totalCollected,
      totalExpected,
      percentPaid: totalPlayers > 0 ? (paidCount / totalPlayers) * 100 : 0,
    };
  }, [payments, players]);

  // Filter and search
  const filteredPayments = useMemo(() => {
    let filtered = payments;

    // Apply status filter
    if (filter === "paid") {
      filtered = filtered.filter((p) => p.paid);
    } else if (filter === "unpaid") {
      filtered = filtered.filter((p) => !p.paid);
    }

    // Apply search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((payment) => {
        const player = players.find((p) => p.id === payment.player_id);
        return player?.name.toLowerCase().includes(term);
      });
    }

    return filtered;
  }, [payments, filter, searchTerm, players]);

  const sendReminders = () => {
    const unpaidPlayers = payments
      .filter((p) => !p.paid)
      .map((p) => players.find((pl) => pl.id === p.player_id))
      .filter((p) => p?.email);

    if (unpaidPlayers.length === 0) {
      alert("All players have paid!");
      return;
    }

    const emails = unpaidPlayers.map((p) => p.email).join(",");
    window.location.href = `mailto:${emails}?subject=Payment Reminder - ${team.name}&body=Hi,\n\nThis is a friendly reminder about your team registration payment for ${season.name}.\n\nThanks!`;
  };

  const exportCSV = () => {
    const csv = [
      [
        "Player Name",
        "Amount",
        "Paid",
        "Payment Date",
        "Payment Method",
        "Notes",
      ].join(","),
      ...payments.map((payment) => {
        const player = players.find((p) => p.id === payment.player_id);
        return [
          player?.name || "Unknown",
          payment.amount || 0,
          payment.paid ? "Yes" : "No",
          payment.paid_date || "",
          payment.payment_method || "",
          payment.notes || "",
        ].join(",");
      }),
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${team.name}-payments-${season.name}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-3xl p-8 border border-gray-100 dark:border-gray-700">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
          <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-3xl p-6 sm:p-8 border border-gray-100 dark:border-gray-700 shadow-lg">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-emerald-500/10 dark:bg-emerald-500/20">
          <DollarSign
            size={22}
            className="text-emerald-600 dark:text-emerald-400"
          />
        </div>
        <h2 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
          Payment Tracker
        </h2>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <motion.div
          whileHover={{ scale: 1.02 }}
          className="bg-gradient-to-br from-emerald-50 to-green-50 dark:from-emerald-950/40 dark:to-green-950/30 
                     rounded-2xl p-5 border-2 border-emerald-200 dark:border-emerald-800"
        >
          <p className="text-xs uppercase tracking-widest text-emerald-600 dark:text-emerald-400 mb-1">
            Paid
          </p>
          <p className="text-3xl font-black text-emerald-600 dark:text-emerald-400">
            {stats.paidCount}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            of {stats.totalPlayers} players
          </p>
        </motion.div>

        <motion.div
          whileHover={{ scale: 1.02 }}
          className="bg-gradient-to-br from-red-50 to-rose-50 dark:from-red-950/40 dark:to-rose-950/30 
                     rounded-2xl p-5 border-2 border-red-200 dark:border-red-800"
        >
          <p className="text-xs uppercase tracking-widest text-red-600 dark:text-red-400 mb-1">
            Unpaid
          </p>
          <p className="text-3xl font-black text-red-600 dark:text-red-400">
            {stats.unpaidCount}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            pending payment
          </p>
        </motion.div>

        <motion.div
          whileHover={{ scale: 1.02 }}
          className="bg-gradient-to-br from-blue-50 to-sky-50 dark:from-blue-950/40 dark:to-sky-950/30 
                     rounded-2xl p-5 border-2 border-blue-200 dark:border-blue-800"
        >
          <p className="text-xs uppercase tracking-widest text-blue-600 dark:text-blue-400 mb-1">
            Collected
          </p>
          <p className="text-3xl font-black text-blue-600 dark:text-blue-400">
            ${stats.totalCollected.toFixed(0)}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            of ${stats.totalExpected.toFixed(0)}
          </p>
        </motion.div>

        <motion.div
          whileHover={{ scale: 1.02 }}
          className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-950/40 dark:to-violet-950/30 
                     rounded-2xl p-5 border-2 border-purple-200 dark:border-purple-800"
        >
          <p className="text-xs uppercase tracking-widest text-purple-600 dark:text-purple-400 mb-1">
            Progress
          </p>
          <p className="text-3xl font-black text-purple-600 dark:text-purple-400">
            {stats.percentPaid.toFixed(0)}%
          </p>
          <div className="mt-2 h-2 bg-purple-200 dark:bg-purple-900 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${stats.percentPaid}%` }}
              transition={{ duration: 1, ease: "easeOut" }}
              className="h-full bg-purple-600 dark:bg-purple-400"
            />
          </div>
        </motion.div>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="flex-1 min-w-[200px] relative">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            size={18}
          />
          <input
            type="text"
            placeholder="Search players..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border-2 border-gray-200 dark:border-gray-700 rounded-2xl 
                       bg-white dark:bg-gray-900 text-gray-900 dark:text-white
                       focus:outline-none focus:ring-2 focus:ring-emerald-500"
          />
        </div>

        <div className="flex gap-2">
          {["all", "paid", "unpaid"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2.5 rounded-2xl font-semibold text-sm transition-all ${
                filter === f
                  ? "bg-emerald-600 text-white shadow-md"
                  : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
              }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={sendReminders}
          className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white 
                     px-5 py-2.5 rounded-2xl font-semibold transition-colors shadow-sm"
        >
          <Mail size={18} /> Remind Unpaid
        </motion.button>

        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={exportCSV}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white 
                     px-5 py-2.5 rounded-2xl font-semibold transition-colors shadow-sm"
        >
          <Download size={18} /> Export
        </motion.button>
      </div>

      {/* Payment List */}
      <div className="space-y-3">
        <AnimatePresence>
          {filteredPayments.map((payment, index) => {
            const player = players.find((p) => p.id === payment.player_id);
            if (!player) return null;

            return (
              <motion.div
                key={payment.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: index * 0.05 }}
                className="bg-gray-50 dark:bg-gray-900/50 rounded-2xl p-5 border-2 border-gray-200 dark:border-gray-700"
                style={{
                  borderLeftWidth: "4px",
                  borderLeftColor: payment.paid ? "#10b981" : "#ef4444",
                }}
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  {/* Player Info */}
                  <div className="flex-1">
                    <h3 className="font-bold text-lg text-gray-900 dark:text-white">
                      {player.name}
                    </h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {player.email || "No email"}
                    </p>
                  </div>

                  {/* Amount */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500 dark:text-gray-400">
                      Amount:
                    </span>
                    <input
                      type="number"
                      value={payment.amount || ""}
                      onChange={(e) =>
                        updatePayment(
                          payment.id,
                          "amount",
                          parseFloat(e.target.value) || 0,
                        )
                      }
                      className="w-24 px-3 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-xl 
                                 bg-white dark:bg-gray-800 text-gray-900 dark:text-white font-semibold"
                      placeholder="0"
                    />
                  </div>

                  {/* Payment Method */}
                  <select
                    value={payment.payment_method || ""}
                    onChange={(e) =>
                      updatePayment(
                        payment.id,
                        "payment_method",
                        e.target.value,
                      )
                    }
                    className="px-3 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-xl 
                               bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
                  >
                    <option value="">Method</option>
                    <option value="cash">Cash</option>
                    <option value="venmo">Venmo</option>
                    <option value="zelle">Zelle</option>
                    <option value="cashapp">Cash App</option>
                    <option value="check">Check</option>
                    <option value="other">Other</option>
                  </select>

                  {/* Paid Toggle */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => togglePaid(payment.id, payment.paid)}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold transition-all ${
                      payment.paid
                        ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                        : "bg-red-600 hover:bg-red-700 text-white"
                    }`}
                  >
                    {payment.paid ? (
                      <>
                        <Check size={18} /> Paid
                      </>
                    ) : (
                      <>
                        <X size={18} /> Unpaid
                      </>
                    )}
                  </motion.button>
                </div>

                {/* Payment Date */}
                {payment.paid && payment.paid_date && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    Paid on {new Date(payment.paid_date).toLocaleDateString()}
                  </p>
                )}
              </motion.div>
            );
          })}
        </AnimatePresence>

        {filteredPayments.length === 0 && (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            No payments found matching your filters.
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentTracker;
